import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type Unsubscribe,
} from "firebase/firestore";
import {
  FORMATIONS_COLLECTION,
  SESSIONS_COLLECTION,
  USERS_COLLECTION,
  firestore,
} from "../lib/firebase";
import type {
  ParticipantAccess,
  SessionStatus,
  UserSessionIndex,
} from "../types/access";
import type { PedagogicalContentKind } from "../types/pedagogicalContent";
import type {
  ExpectedParticipant,
  ExpectedParticipantDraft,
  ParticipantOnboardingStatus,
  SessionActivationSnapshot,
  SessionChecklist,
  SessionClosureSnapshot,
  SessionClosureSummary,
  SessionFullContentProjection,
  SessionFormationAttachment,
  SessionPublicDeckProjection,
  SessionPublicExerciseProjection,
  SessionPublicFormationProjection,
  SessionPublicQuestionnaireProjection,
  SessionRuntimeState,
  SessionShortCodeStatus,
  SessionTrainer,
  SessionTrainerRole,
  TrainingSession,
  TrainingSessionDraft,
} from "../types/session";
import type { FormationContentState } from "../versioning/formationContentGraph";
import { asRecord, normalizeEmail, toDate } from "../utils/firestoreData";
import {
  listCurrentUserSessions,
  listSessionPublicExercises,
  listSessionPublicQuestionnaires,
} from "./accessProjectionService";
import { writeAuditEvent } from "./auditService";
import {
  listSessionAttendanceStatuses,
  listSessionAttendances,
} from "./attendanceService";
import {
  ensureCurrentFormationPublicationSnapshot,
  freezeCurrentFormationPublicationSnapshot,
  readCurrentPublishedFormationContentSnapshot,
} from "./formationContentVersioningService";
import {
  listPedagogicalContent,
  publishDeckVersion,
  publishExerciseVersion,
  publishQuestionnaireVersion,
} from "./pedagogicalContentService";

const USER_SESSIONS_COLLECTION = "sessions";
const SESSION_FORMATIONS_COLLECTION = "formations";
const SESSION_TRAINERS_COLLECTION = "formationAdmins";
const SESSION_PARTICIPANTS_COLLECTION = "participants";
const CONTENT_TAGS_COLLECTION = "contentTags";
const CONTENT_TAG_RESOURCES_COLLECTION = "resources";
const SESSION_PUBLIC_COLLECTION = "sessionPublic";
const SESSION_FULL_COLLECTION = "sessionFull";
const SESSION_FULL_CONTENT_COLLECTION = "content";
const SESSION_RUNTIME_COLLECTION = "sessionRuntime";
const PUBLIC_DECKS_COLLECTION = "decks";
const PUBLIC_EXERCISES_COLLECTION = "exercises";
const PUBLIC_QUESTIONNAIRES_COLLECTION = "questionnaires";
const SESSION_SHORT_CODES_COLLECTION = "sessionShortCodes";
const SHORT_CODE_ALPHABET = "23456789ABCDEFGHJKLMNPRSTUVWXYZ";

export const defaultSessionChecklist: SessionChecklist = {
  accessibilityChecked: false,
  companyAgreement: false,
  materialsReady: false,
  participantListReady: false,
  roomReady: false,
};

function normalizeSessionStatus(value: unknown): SessionStatus {
  if (
    value === "planned" ||
    value === "active" ||
    value === "ended" ||
    value === "archived" ||
    value === "cancelled"
  ) {
    return value;
  }

  return "draft";
}

function normalizeShortCodeStatus(value: unknown): SessionShortCodeStatus {
  if (value === "active" || value === "disabled") {
    return value;
  }

  return "inactive";
}

function normalizeTrainerRole(value: unknown): SessionTrainerRole {
  return value === "trainer" ? "trainer" : "sessionAdmin";
}

function normalizeOnboardingStatus(value: unknown): ParticipantOnboardingStatus {
  if (
    value === "pending_auth" ||
    value === "linked" ||
    value === "access_requested" ||
    value === "disabled"
  ) {
    return value;
  }

  return "not_started";
}

function normalizeChecklist(value: unknown): SessionChecklist {
  const data = asRecord(value);

  return {
    accessibilityChecked: data.accessibilityChecked === true,
    companyAgreement: data.companyAgreement === true,
    materialsReady: data.materialsReady === true,
    participantListReady: data.participantListReady === true,
    roomReady: data.roomReady === true,
  };
}

function normalizeSession(
  id: string,
  data: Record<string, unknown>,
): TrainingSession {
  return {
    archivedAt: toDate(data.archivedAt),
    archivedBy: typeof data.archivedBy === "string" ? data.archivedBy : null,
    authorUid: typeof data.authorUid === "string" ? data.authorUid : "",
    checklist: normalizeChecklist(data.checklist),
    companyTrainingManagerEmail:
      typeof data.companyTrainingManagerEmail === "string"
        ? data.companyTrainingManagerEmail
        : "",
    companyTrainingManagerFirstName:
      typeof data.companyTrainingManagerFirstName === "string"
        ? data.companyTrainingManagerFirstName
        : "",
    companyTrainingManagerLastName:
      typeof data.companyTrainingManagerLastName === "string"
        ? data.companyTrainingManagerLastName
        : "",
    contentTagId: typeof data.contentTagId === "string" ? data.contentTagId : null,
    createdAt: toDate(data.createdAt),
    deletedAt: toDate(data.deletedAt),
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : null,
    endedAt: toDate(data.endedAt),
    endedBy: typeof data.endedBy === "string" ? data.endedBy : null,
    endAt: toDate(data.endAt),
    id: typeof data.id === "string" ? data.id : id,
    shortCode: typeof data.shortCode === "string" ? data.shortCode : "",
    shortCodeActivatedAt: toDate(data.shortCodeActivatedAt),
    shortCodeDisabledAt: toDate(data.shortCodeDisabledAt),
    shortCodeStatus: normalizeShortCodeStatus(data.shortCodeStatus),
    startAt: toDate(data.startAt),
    status: normalizeSessionStatus(data.status),
    timezone: typeof data.timezone === "string" ? data.timezone : "Europe/Paris",
    title: typeof data.title === "string" ? data.title : "Session sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeFormationAttachment(
  id: string,
  data: Record<string, unknown>,
): SessionFormationAttachment {
  return {
    attachedAt: toDate(data.attachedAt),
    attachedBy: typeof data.attachedBy === "string" ? data.attachedBy : "",
    formationId: typeof data.formationId === "string" ? data.formationId : id,
    order: typeof data.order === "number" ? data.order : 0,
    title: typeof data.title === "string" ? data.title : "Formation",
  };
}

function normalizeTrainer(id: string, data: Record<string, unknown>): SessionTrainer {
  return {
    createdAt: toDate(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    role: normalizeTrainerRole(data.role),
    uid: typeof data.uid === "string" ? data.uid : id,
  };
}

function normalizeParticipant(
  id: string,
  data: Record<string, unknown>,
): ExpectedParticipant {
  return {
    createdAt: toDate(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    email: typeof data.email === "string" ? data.email : "",
    firstName: typeof data.firstName === "string" ? data.firstName : "",
    id: typeof data.id === "string" ? data.id : id,
    lastAuthAt: toDate(data.lastAuthAt),
    lastName: typeof data.lastName === "string" ? data.lastName : "",
    onboardingCompletedAt: toDate(data.onboardingCompletedAt),
    onboardingStatus: normalizeOnboardingStatus(data.onboardingStatus),
    uid: typeof data.uid === "string" ? data.uid : null,
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeSessionRuntimeState(
  id: string,
  data: Record<string, unknown>,
): SessionRuntimeState {
  const currentPage =
    typeof data.currentPage === "number" ? Math.trunc(data.currentPage) : 0;

  return {
    contentTagId: typeof data.contentTagId === "string" ? data.contentTagId : "",
    currentDeckId:
      typeof data.currentDeckId === "string" ? data.currentDeckId : null,
    currentPage: Math.max(0, currentPage),
    id: typeof data.id === "string" ? data.id : id,
    initializedAt: toDate(data.initializedAt),
    initializedBy: typeof data.initializedBy === "string" ? data.initializedBy : "",
    liveSummary: typeof data.liveSummary === "string" ? data.liveSummary : "Accueil",
    sessionId: typeof data.sessionId === "string" ? data.sessionId : id,
    status: data.status === "ended" ? "ended" : "active",
    updatedAt: toDate(data.updatedAt),
  };
}

function createConverter<T>(
  normalize: (id: string, data: Record<string, unknown>) => T,
): FirestoreDataConverter<T> {
  return {
    fromFirestore(
      snapshot: QueryDocumentSnapshot<DocumentData>,
      options: SnapshotOptions,
    ): T {
      return normalize(snapshot.id, asRecord(snapshot.data(options)));
    },
    toFirestore(value: T): DocumentData {
      return value as DocumentData;
    },
  };
}

const sessionConverter = createConverter(normalizeSession);
const sessionRuntimeConverter = createConverter(normalizeSessionRuntimeState);

function requireData<T>(value: T | undefined, label: string): T {
  if (!value) {
    throw new Error(`${label} introuvable après écriture.`);
  }

  return value;
}

function sessionRef(sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSIONS_COLLECTION, sessionId).withConverter(
    sessionConverter,
  );
}

function sessionSubDocRef(
  sessionId: string,
  collectionName: string,
  documentId: string,
) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSIONS_COLLECTION, sessionId, collectionName, documentId);
}

function userSessionIndexRef(uid: string, sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    USERS_COLLECTION,
    uid,
    USER_SESSIONS_COLLECTION,
    sessionId,
  );
}

function contentTagRef(contentTagId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, CONTENT_TAGS_COLLECTION, contentTagId);
}

function contentTagResourceRef(contentTagId: string, resourceKey: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    CONTENT_TAGS_COLLECTION,
    contentTagId,
    CONTENT_TAG_RESOURCES_COLLECTION,
    resourceKey,
  );
}

function sessionPublicRef(sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSION_PUBLIC_COLLECTION, sessionId);
}

function sessionPublicSubDocRef(
  sessionId: string,
  collectionName: string,
  documentId: string,
) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSION_PUBLIC_COLLECTION, sessionId, collectionName, documentId);
}

function sessionFullRef(sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSION_FULL_COLLECTION, sessionId);
}

function sessionFullContentRef(sessionId: string, resourceKey: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_FULL_COLLECTION,
    sessionId,
    SESSION_FULL_CONTENT_COLLECTION,
    resourceKey,
  );
}

function sessionRuntimeRef(sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSION_RUNTIME_COLLECTION, sessionId);
}

function sessionShortCodeRef(shortCode: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSION_SHORT_CODES_COLLECTION, shortCode);
}

function contentVersionDocRef({
  contentId,
  formationId,
  kind,
  versionId,
}: {
  contentId: string;
  formationId: string;
  kind: PedagogicalContentKind;
  versionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    FORMATIONS_COLLECTION,
    formationId,
    `${kind}s`,
    contentId,
    "versions",
    versionId,
  );
}

function randomShortCode(): string {
  const values = crypto.getRandomValues(new Uint32Array(5));
  return [...values]
    .map((value) => SHORT_CODE_ALPHABET[value % SHORT_CODE_ALPHABET.length])
    .join("");
}

function assertSessionDraft(draft: TrainingSessionDraft): TrainingSessionDraft {
  const title = draft.title.trim();

  if (!title) {
    throw new Error("Le titre de la session est obligatoire.");
  }

  if (draft.startAt && draft.endAt && draft.endAt <= draft.startAt) {
    throw new Error("La date de fin doit être postérieure à la date de début.");
  }

  return {
    ...draft,
    companyTrainingManagerEmail: normalizeEmail(
      draft.companyTrainingManagerEmail,
    ),
    companyTrainingManagerFirstName:
      draft.companyTrainingManagerFirstName.trim(),
    companyTrainingManagerLastName: draft.companyTrainingManagerLastName.trim(),
    timezone: draft.timezone.trim() || "Europe/Paris",
    title,
  };
}

async function upsertSessionIndex({
  participantSlotId = null,
  role,
  session,
  uid,
}: {
  participantSlotId?: string | null;
  role: UserSessionIndex["role"];
  session: TrainingSession;
  uid: string;
}): Promise<void> {
  await setDoc(
    userSessionIndexRef(uid, session.id),
    {
      publicEndAt: session.endAt,
      publicStartAt: session.startAt,
      participantSlotId,
      role,
      sessionId: session.id,
      status: session.status,
      title: session.title,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

function normalizeParticipantAccess(
  uid: string,
  data: Record<string, unknown>,
): ParticipantAccess {
  return {
    createdAt: toDate(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : null,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : "",
    status: data.status === "disabled" ? "disabled" : "active",
    uid: typeof data.uid === "string" ? data.uid : uid,
  };
}

async function listParticipantAccesses(
  sessionId: string,
): Promise<ParticipantAccess[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    collection(firestore, SESSIONS_COLLECTION, sessionId, "participantAccess"),
  );

  return snapshot.docs.map((accessDoc) =>
    normalizeParticipantAccess(accessDoc.id, asRecord(accessDoc.data())),
  );
}

async function syncSessionIndexes(session: TrainingSession): Promise<void> {
  const trainers = await listSessionTrainers(session.id);

  await Promise.all([
    upsertSessionIndex({
      role: "author",
      session,
      uid: session.authorUid,
    }),
    ...trainers.map((trainer) =>
      upsertSessionIndex({
        role: trainer.role === "trainer" ? "trainer" : "sessionAdmin",
        session,
        uid: trainer.uid,
      }),
    ),
  ]);
}

type PublishedDeckSnapshot = FormationContentState["decks"][string];
type PublishedQuestionnaireSnapshot =
  FormationContentState["questionnaires"][string];
type PublishedExerciseSnapshot = FormationContentState["exercises"][string];

type VersionedDeck = {
  content: PublishedDeckSnapshot;
  contentId: string;
  kind: "deck";
  order: number;
  versionId: string;
  versionSnapshot: Record<string, unknown>;
};

type VersionedQuestionnaire = {
  content: PublishedQuestionnaireSnapshot;
  contentId: string;
  kind: "questionnaire";
  order: number;
  versionId: string;
  versionSnapshot: Record<string, unknown>;
};

type VersionedExercise = {
  content: PublishedExerciseSnapshot;
  contentId: string;
  kind: "exercise";
  order: number;
  versionId: string;
  versionSnapshot: Record<string, unknown>;
};

type VersionedContent =
  | VersionedDeck
  | VersionedQuestionnaire
  | VersionedExercise;

type SessionActivationPlan = {
  decks: VersionedDeck[];
  exercises: VersionedExercise[];
  formationProjections: SessionPublicFormationProjection[];
  formationResources: SessionFullContentProjection[];
  questionnaires: VersionedQuestionnaire[];
};

export type SessionContentFreezeResult = {
  formationCount: number;
  publishedVersionCount: number;
  refreshedCount: number;
  sessionId: string;
  snapshotCount: number;
};

async function publishMissingPedagogicalVersions({
  actorUid,
  formationId,
  sessionTitle,
}: {
  actorUid: string;
  formationId: string;
  sessionTitle: string;
}): Promise<number> {
  const content = await listPedagogicalContent(formationId);
  let publishedCount = 0;

  for (const deck of content.decks) {
    if (!deck.currentPublishedVersionId) {
      await publishDeckVersion({
        actorUid,
        deck,
        message: `Gel session ${sessionTitle} - deck ${deck.title}`,
      });
      publishedCount += 1;
    }
  }

  for (const questionnaire of content.questionnaires) {
    if (!questionnaire.currentPublishedVersionId) {
      await publishQuestionnaireVersion({
        actorUid,
        message: `Gel session ${sessionTitle} - questionnaire ${questionnaire.title}`,
        questionnaire,
      });
      publishedCount += 1;
    }
  }

  for (const exercise of content.exercises) {
    if (!exercise.currentPublishedVersionId) {
      await publishExerciseVersion({
        actorUid,
        exercise,
        message: `Gel session ${sessionTitle} - exercice ${exercise.title}`,
      });
      publishedCount += 1;
    }
  }

  return publishedCount;
}

async function freezeAttachedFormationSnapshots({
  actorUid,
  onlyMissing,
  session,
}: {
  actorUid: string;
  onlyMissing: boolean;
  session: TrainingSession;
}): Promise<SessionContentFreezeResult> {
  const attachments = await listSessionFormations(session.id);

  if (attachments.length === 0) {
    throw new Error("Au moins une formation doit être attachée avant gel des contenus.");
  }

  const results = await Promise.all(
    attachments.map(async (attachment) => {
      const message = `Gel contenu session ${session.title} - ${attachment.title}`;
      const publishedVersionCount = await publishMissingPedagogicalVersions({
        actorUid,
        formationId: attachment.formationId,
        sessionTitle: session.title,
      });
      const snapshot =
        publishedVersionCount > 0 || !onlyMissing
          ? await freezeCurrentFormationPublicationSnapshot({
              actorUid,
              formationId: attachment.formationId,
              message,
            })
          : await ensureCurrentFormationPublicationSnapshot({
              actorUid,
              formationId: attachment.formationId,
              message,
            });

      return {
        publishedVersionCount,
        snapshot,
      };
    }),
  );

  return {
    formationCount: attachments.length,
    publishedVersionCount: results.reduce(
      (total, result) => total + result.publishedVersionCount,
      0,
    ),
    refreshedCount: results.filter((result) => result.snapshot.created).length,
    sessionId: session.id,
    snapshotCount: results.filter((result) => result.snapshot.revision !== null)
      .length,
  };
}

function assertPublishedVersion({
  contentTitle,
  kindLabel,
  versionId,
}: {
  contentTitle: string;
  kindLabel: string;
  versionId: string | null;
}): string {
  if (!versionId) {
    throw new Error(`${kindLabel} "${contentTitle}" n'a pas de version publiée.`);
  }

  return versionId;
}

function snapshotString(
  snapshot: Record<string, unknown>,
  key: string,
  fallback: string,
): string {
  return typeof snapshot[key] === "string" ? snapshot[key] : fallback;
}

function snapshotNumber(
  snapshot: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  return typeof snapshot[key] === "number" ? snapshot[key] : fallback;
}

function snapshotRecord<TValue extends object>(
  snapshot: TValue,
): Record<string, unknown> {
  return Object.fromEntries(Object.entries(snapshot)) as Record<string, unknown>;
}

function comparePublishedSnapshots(
  left: { readonly title: string; readonly updatedAtIso: string | null },
  right: { readonly title: string; readonly updatedAtIso: string | null },
): number {
  if (left.updatedAtIso !== right.updatedAtIso) {
    if (left.updatedAtIso === null) {
      return 1;
    }

    if (right.updatedAtIso === null) {
      return -1;
    }

    return right.updatedAtIso.localeCompare(left.updatedAtIso);
  }

  return left.title.localeCompare(right.title, "fr");
}

async function readVersionSnapshot({
  contentId,
  formationId,
  kind,
  versionId,
}: {
  contentId: string;
  formationId: string;
  kind: PedagogicalContentKind;
  versionId: string;
}): Promise<Record<string, unknown>> {
  const snapshot = await getDoc(
    contentVersionDocRef({
      contentId,
      formationId,
      kind,
      versionId,
    }),
  );

  if (!snapshot.exists()) {
    throw new Error(`Version publiée introuvable pour ${kind} ${contentId}.`);
  }

  return asRecord(snapshot.data());
}

async function buildActivationPlan(
  session: TrainingSession,
): Promise<SessionActivationPlan> {
  const attachments = await listSessionFormations(session.id);

  if (attachments.length === 0) {
    throw new Error("Au moins une formation doit être attachée avant démarrage.");
  }

  const plan: SessionActivationPlan = {
    decks: [],
    exercises: [],
    formationProjections: [],
    formationResources: [],
    questionnaires: [],
  };

  for (const attachment of attachments) {
    const publishedSnapshot =
      await readCurrentPublishedFormationContentSnapshot(attachment.formationId);
    const formationVersionId = String(publishedSnapshot.revision);
    const formationTitle = publishedSnapshot.state.formation.title;

    plan.formationProjections.push({
      id: attachment.formationId,
      order: attachment.order,
      sessionId: session.id,
      sourceFormationId: attachment.formationId,
      title: formationTitle,
      versionId: formationVersionId,
    });
    plan.formationResources.push({
      fullSnapshot: snapshotRecord(publishedSnapshot.state.formation),
      order: attachment.order,
      resourceKey: `formation_${attachment.formationId}`,
      resourceType: "formation",
      sessionId: session.id,
      sourceId: attachment.formationId,
      sourcePath: `formationContentVcs/${attachment.formationId}/revisions/${publishedSnapshot.revision}`,
      versionId: formationVersionId,
    });

    const decks = Object.values(publishedSnapshot.state.decks).sort(
      comparePublishedSnapshots,
    );
    const questionnaires = Object.values(
      publishedSnapshot.state.questionnaires,
    ).sort(comparePublishedSnapshots);
    const exercises = Object.values(publishedSnapshot.state.exercises).sort(
      comparePublishedSnapshots,
    );

    for (const deck of decks) {
      const versionId = assertPublishedVersion({
        contentTitle: deck.title,
        kindLabel: "Le deck",
        versionId: deck.currentPublishedVersionId,
      });
      plan.decks.push({
        content: deck,
        contentId: deck.deckId,
        kind: "deck",
        order: plan.decks.length + 1,
        versionId,
        versionSnapshot: await readVersionSnapshot({
          contentId: deck.deckId,
          formationId: deck.formationId,
          kind: "deck",
          versionId,
        }),
      });
    }

    for (const questionnaire of questionnaires) {
      const versionId = assertPublishedVersion({
        contentTitle: questionnaire.title,
        kindLabel: "Le questionnaire",
        versionId: questionnaire.currentPublishedVersionId,
      });
      plan.questionnaires.push({
        content: questionnaire,
        contentId: questionnaire.questionnaireId,
        kind: "questionnaire",
        order: plan.questionnaires.length + 1,
        versionId,
        versionSnapshot: await readVersionSnapshot({
          contentId: questionnaire.questionnaireId,
          formationId: questionnaire.formationId,
          kind: "questionnaire",
          versionId,
        }),
      });
    }

    for (const exercise of exercises) {
      const versionId = assertPublishedVersion({
        contentTitle: exercise.title,
        kindLabel: "L'exercice",
        versionId: exercise.currentPublishedVersionId,
      });
      plan.exercises.push({
        content: exercise,
        contentId: exercise.exerciseId,
        kind: "exercise",
        order: plan.exercises.length + 1,
        versionId,
        versionSnapshot: await readVersionSnapshot({
          contentId: exercise.exerciseId,
          formationId: exercise.formationId,
          kind: "exercise",
          versionId,
        }),
      });
    }
  }

  return plan;
}

function resourceProjection({
  resource,
  sessionId,
}: {
  resource: VersionedContent;
  sessionId: string;
}): SessionFullContentProjection {
  return {
    fullSnapshot: resource.versionSnapshot,
    order: resource.order,
    resourceKey: `${resource.kind}_${resource.content.formationId}_${resource.contentId}`,
    resourceType: resource.kind,
    sessionId,
    sourceId: resource.contentId,
    sourcePath: `formations/${resource.content.formationId}/${resource.kind}s/${resource.contentId}/versions/${resource.versionId}`,
    versionId: resource.versionId,
  };
}

export async function createTrainingSession({
  actorUid,
  draft,
}: {
  actorUid: string;
  draft: TrainingSessionDraft;
}): Promise<TrainingSession> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const normalizedDraft = assertSessionDraft(draft);
  const nextSessionRef = doc(collection(firestore, SESSIONS_COLLECTION));
  const sessionId = nextSessionRef.id;
  const shortCode = randomShortCode();

  await setDoc(nextSessionRef, {
    authorUid: actorUid,
    checklist: normalizedDraft.checklist,
    companyTrainingManagerEmail:
      normalizedDraft.companyTrainingManagerEmail,
    companyTrainingManagerFirstName:
      normalizedDraft.companyTrainingManagerFirstName,
    companyTrainingManagerLastName:
      normalizedDraft.companyTrainingManagerLastName,
    createdAt: serverTimestamp(),
    endAt: normalizedDraft.endAt,
    id: sessionId,
    shortCode,
    shortCodeStatus: "inactive",
    startAt: normalizedDraft.startAt,
    status: "draft",
    timezone: normalizedDraft.timezone,
    title: normalizedDraft.title,
    updatedAt: serverTimestamp(),
  });

  await setDoc(
    sessionSubDocRef(sessionId, SESSION_TRAINERS_COLLECTION, actorUid),
    {
      createdAt: serverTimestamp(),
      createdBy: actorUid,
      role: "sessionAdmin",
      uid: actorUid,
    },
  );

  const snapshot = await getDoc(sessionRef(sessionId));
  const session = requireData(snapshot.data(), "Session");
  await upsertSessionIndex({
    role: "author",
    session,
    uid: actorUid,
  });
  await writeAuditEvent({
    actorUid,
    eventType: "session.created",
    metadata: {
      sessionId,
      shortCode,
    },
  });

  return session;
}

export async function readTrainingSession(
  sessionId: string,
): Promise<TrainingSession | null> {
  const snapshot = await getDoc(sessionRef(sessionId));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function listAccessibleTrainingSessions({
  isGlobalAdmin,
  uid,
}: {
  isGlobalAdmin: boolean;
  uid: string;
}): Promise<TrainingSession[]> {
  if (!firestore) {
    return [];
  }

  if (isGlobalAdmin) {
    const sessionsQuery = query(
      collection(firestore, SESSIONS_COLLECTION).withConverter(sessionConverter),
      orderBy("updatedAt", "desc"),
    );
    const snapshot = await getDocs(sessionsQuery);
    return snapshot.docs
      .map((sessionDoc) => sessionDoc.data())
      .filter((session) => session.deletedAt === null);
  }

  const indexes = await listCurrentUserSessions(uid);
  const sessions = await Promise.all(
    indexes.map((index) => readTrainingSession(index.sessionId)),
  );
  return sessions.filter(
    (session): session is TrainingSession =>
      session !== null && session.deletedAt === null,
  );
}

export async function updateTrainingSessionMetadata({
  actorUid,
  draft,
  sessionId,
}: {
  actorUid: string;
  draft: TrainingSessionDraft;
  sessionId: string;
}): Promise<TrainingSession> {
  const normalizedDraft = assertSessionDraft(draft);

  await updateDoc(sessionRef(sessionId), {
    checklist: normalizedDraft.checklist,
    companyTrainingManagerEmail:
      normalizedDraft.companyTrainingManagerEmail,
    companyTrainingManagerFirstName:
      normalizedDraft.companyTrainingManagerFirstName,
    companyTrainingManagerLastName:
      normalizedDraft.companyTrainingManagerLastName,
    endAt: normalizedDraft.endAt,
    startAt: normalizedDraft.startAt,
    timezone: normalizedDraft.timezone,
    title: normalizedDraft.title,
    updatedAt: serverTimestamp(),
  });

  const session = await readTrainingSession(sessionId);

  if (!session) {
    throw new Error("Session introuvable après modification.");
  }

  await syncSessionIndexes(session);
  await writeAuditEvent({
    actorUid,
    eventType: "session.updated",
    metadata: {
      sessionId,
    },
  });

  return session;
}

export async function attachFormationToSession({
  actorUid,
  formationId,
  session,
  title,
}: {
  actorUid: string;
  formationId: string;
  session: TrainingSession;
  title: string;
}): Promise<SessionFormationAttachment> {
  const currentAttachments = await listSessionFormations(session.id);
  const order = currentAttachments.length + 1;

  await setDoc(
    sessionSubDocRef(session.id, SESSION_FORMATIONS_COLLECTION, formationId),
    {
      attachedAt: serverTimestamp(),
      attachedBy: actorUid,
      formationId,
      order,
      title,
    },
    { merge: true },
  );
  await writeAuditEvent({
    actorUid,
    eventType: "session.formation.attached",
    metadata: {
      formationId,
      sessionId: session.id,
    },
  });

  return {
    attachedAt: new Date(),
    attachedBy: actorUid,
    formationId,
    order,
    title,
  };
}

export async function listSessionFormations(
  sessionId: string,
): Promise<SessionFormationAttachment[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      collection(
        firestore,
        SESSIONS_COLLECTION,
        sessionId,
        SESSION_FORMATIONS_COLLECTION,
      ),
      orderBy("order", "asc"),
    ),
  );

  return snapshot.docs.map((formationDoc) =>
    normalizeFormationAttachment(formationDoc.id, asRecord(formationDoc.data())),
  );
}

export async function addSessionTrainer({
  actorUid,
  role,
  session,
  trainerUid,
}: {
  actorUid: string;
  role: SessionTrainerRole;
  session: TrainingSession;
  trainerUid: string;
}): Promise<SessionTrainer> {
  const uid = trainerUid.trim();

  if (!uid) {
    throw new Error("L'UID formateur est obligatoire.");
  }

  await setDoc(sessionSubDocRef(session.id, SESSION_TRAINERS_COLLECTION, uid), {
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    role,
    uid,
  });
  await upsertSessionIndex({
    role: role === "trainer" ? "trainer" : "sessionAdmin",
    session,
    uid,
  });
  await writeAuditEvent({
    actorUid,
    eventType: "session.trainer.added",
    metadata: {
      role,
      sessionId: session.id,
      trainerUid: uid,
    },
  });

  return {
    createdAt: new Date(),
    createdBy: actorUid,
    role,
    uid,
  };
}

export async function listSessionTrainers(
  sessionId: string,
): Promise<SessionTrainer[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    collection(
      firestore,
      SESSIONS_COLLECTION,
      sessionId,
      SESSION_TRAINERS_COLLECTION,
    ),
  );

  return snapshot.docs
    .map((trainerDoc) => normalizeTrainer(trainerDoc.id, asRecord(trainerDoc.data())))
    .sort((left, right) => left.uid.localeCompare(right.uid, "fr"));
}

export async function addExpectedParticipant({
  actorUid,
  draft,
  sessionId,
}: {
  actorUid: string;
  draft: ExpectedParticipantDraft;
  sessionId: string;
}): Promise<ExpectedParticipant> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();

  if (!firstName || !lastName) {
    throw new Error("Le prénom et le nom du participant sont obligatoires.");
  }

  const participantRef = doc(
    collection(
      firestore,
      SESSIONS_COLLECTION,
      sessionId,
      SESSION_PARTICIPANTS_COLLECTION,
    ),
  );
  const participantId = participantRef.id;
  const email = normalizeEmail(draft.email);

  await setDoc(participantRef, {
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    email,
    firstName,
    id: participantId,
    lastName,
    onboardingStatus: "not_started",
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "session.participant.expected_added",
    metadata: {
      participantId,
      sessionId,
    },
  });

  const snapshot = await getDoc(participantRef);
  return normalizeParticipant(participantId, asRecord(snapshot.data()));
}

export async function listExpectedParticipants(
  sessionId: string,
): Promise<ExpectedParticipant[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    collection(
      firestore,
      SESSIONS_COLLECTION,
      sessionId,
      SESSION_PARTICIPANTS_COLLECTION,
    ),
  );

  return snapshot.docs
    .map((participantDoc) =>
      normalizeParticipant(participantDoc.id, asRecord(participantDoc.data())),
    )
    .sort((left, right) =>
      `${left.lastName} ${left.firstName}`.localeCompare(
        `${right.lastName} ${right.firstName}`,
        "fr",
      ),
    );
}

export async function finalizeTrainingSession({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<TrainingSession> {
  const [formations, trainers, participants] = await Promise.all([
    listSessionFormations(session.id),
    listSessionTrainers(session.id),
    listExpectedParticipants(session.id),
  ]);

  if (session.status !== "draft") {
    throw new Error("Seules les sessions en brouillon peuvent être finalisées.");
  }

  if (formations.length === 0) {
    throw new Error("Au moins une formation doit être attachée.");
  }

  if (trainers.length === 0) {
    throw new Error("Au moins un formateur ou administrateur de session est requis.");
  }

  if (!session.startAt || !session.endAt || session.endAt <= session.startAt) {
    throw new Error("Les dates de session sont incomplètes ou incohérentes.");
  }

  if (
    !session.companyTrainingManagerEmail ||
    !session.companyTrainingManagerFirstName ||
    !session.companyTrainingManagerLastName
  ) {
    throw new Error("La responsable formation de l'entreprise est incomplète.");
  }

  if (!Object.values(session.checklist).every(Boolean)) {
    throw new Error("La checklist logistique doit être entièrement validée.");
  }

  if (participants.length === 0) {
    throw new Error("Au moins un participant attendu doit être préparé.");
  }

  await updateDoc(sessionRef(session.id), {
    status: "planned",
    updatedAt: serverTimestamp(),
  });

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après finalisation.");
  }

  await syncSessionIndexes(nextSession);
  await writeAuditEvent({
    actorUid,
    eventType: "session.finalized",
    metadata: {
      sessionId: session.id,
    },
  });

  return nextSession;
}

export async function startTrainingSession({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<SessionActivationSnapshot> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  if (session.status !== "planned") {
    throw new Error("Seules les sessions planifiées peuvent être démarrées.");
  }

  await freezeAttachedFormationSnapshots({
    actorUid,
    onlyMissing: true,
    session,
  });

  const plan = await buildActivationPlan(session);
  const contentTagId = session.contentTagId ?? `session_${session.id}`;
  const contentResources = [
    ...plan.formationResources,
    ...plan.decks.map((resource) =>
      resourceProjection({
        resource,
        sessionId: session.id,
      }),
    ),
    ...plan.questionnaires.map((resource) =>
      resourceProjection({
        resource,
        sessionId: session.id,
      }),
    ),
    ...plan.exercises.map((resource) =>
      resourceProjection({
        resource,
        sessionId: session.id,
      }),
    ),
  ];
  const resourceCount = contentResources.length;
  const now = new Date();
  const firstDeck = plan.decks[0] ?? null;
  const firstDeckId = firstDeck
    ? `deck_${firstDeck.content.formationId}_${firstDeck.contentId}`
    : null;
  const runtime: SessionRuntimeState = {
    contentTagId,
    currentDeckId: firstDeckId,
    currentPage: 0,
    id: session.id,
    initializedAt: now,
    initializedBy: actorUid,
    liveSummary: "Accueil",
    sessionId: session.id,
    status: "active",
    updatedAt: now,
  };
  const batch = writeBatch(firestore);

  batch.set(
    contentTagRef(contentTagId),
    {
      createdAt: serverTimestamp(),
      createdBy: actorUid,
      id: contentTagId,
      referenceCount: 1,
      resourceCount,
      sessionId: session.id,
      type: "session",
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  for (const resource of contentResources) {
    batch.set(contentTagResourceRef(contentTagId, resource.resourceKey), {
      order: resource.order,
      resourceKey: resource.resourceKey,
      resourceType: resource.resourceType,
      sessionId: session.id,
      sourceId: resource.sourceId,
      sourcePath: resource.sourcePath,
      versionId: resource.versionId,
    });
    batch.set(sessionFullContentRef(session.id, resource.resourceKey), resource);
  }

  batch.set(sessionPublicRef(session.id), {
    contentTagId,
    currentHalfDay: null,
    id: session.id,
    liveSummary: "Accueil",
    publicEndAt: session.endAt,
    publicStartAt: session.startAt,
    status: "active",
    timezone: session.timezone,
    title: session.title,
    updatedAt: serverTimestamp(),
  });

  for (const formation of plan.formationProjections) {
    batch.set(
      sessionPublicSubDocRef(
        session.id,
        SESSION_FORMATIONS_COLLECTION,
        formation.id,
      ),
      formation,
    );
  }

  for (const deckResource of plan.decks) {
    const deck = deckResource.content;
    const publicDeck: SessionPublicDeckProjection = {
      id: `deck_${deck.formationId}_${deckResource.contentId}`,
      order: deckResource.order,
      pageCount: snapshotNumber(
        deckResource.versionSnapshot,
        "pageCount",
        deck.pageCount,
      ),
      sessionId: session.id,
      sourceDeckId: deckResource.contentId,
      title: snapshotString(deckResource.versionSnapshot, "title", deck.title),
      versionId: deckResource.versionId,
      visibleMaxPage: 0,
    };
    batch.set(
      sessionPublicSubDocRef(
        session.id,
        PUBLIC_DECKS_COLLECTION,
        publicDeck.id,
      ),
      publicDeck,
    );
  }

  for (const questionnaireResource of plan.questionnaires) {
    const questionnaire = questionnaireResource.content;
    const publicQuestionnaire: SessionPublicQuestionnaireProjection = {
      closedAt: null,
      closedToNewEntriesAt: null,
      correctionVisible: false,
      correctionVisibleAt: null,
      id: `questionnaire_${questionnaire.formationId}_${questionnaireResource.contentId}`,
      openedAt: null,
      order: questionnaireResource.order,
      runtimeStatus: "not_opened",
      sessionId: session.id,
      sourceQuestionnaireId: questionnaireResource.contentId,
      title: snapshotString(
        questionnaireResource.versionSnapshot,
        "title",
        questionnaire.title,
      ),
      versionId: questionnaireResource.versionId,
      yamlDefinition: snapshotString(
        questionnaireResource.versionSnapshot,
        "yamlDefinition",
        questionnaire.source,
      ),
    };
    batch.set(
      sessionPublicSubDocRef(
        session.id,
        PUBLIC_QUESTIONNAIRES_COLLECTION,
        publicQuestionnaire.id,
      ),
      publicQuestionnaire,
    );
  }

  for (const exerciseResource of plan.exercises) {
    const exercise = exerciseResource.content;
    const publicExercise: SessionPublicExerciseProjection = {
      activatedAt: null,
      correctionDeckFragment: snapshotString(
        exerciseResource.versionSnapshot,
        "correctionDeckFragment",
        exercise.correctionDeckFragment,
      ),
      correctionMarkdown: snapshotString(
        exerciseResource.versionSnapshot,
        "correctionMarkdown",
        exercise.correctionMarkdown,
      ),
      correctionVisible: false,
      correctionVisibleAt: null,
      id: `exercise_${exercise.formationId}_${exerciseResource.contentId}`,
      order: exerciseResource.order,
      passedAt: null,
      responseMode: exercise.responseMode,
      runtimeStatus: "not_opened",
      sessionId: session.id,
      sourceExerciseId: exerciseResource.contentId,
      statementMarkdown: snapshotString(
        exerciseResource.versionSnapshot,
        "statementMarkdown",
        exercise.source,
      ),
      title: snapshotString(
        exerciseResource.versionSnapshot,
        "title",
        exercise.title,
      ),
      versionId: exerciseResource.versionId,
    };
    batch.set(
      sessionPublicSubDocRef(
        session.id,
        PUBLIC_EXERCISES_COLLECTION,
        publicExercise.id,
      ),
      publicExercise,
    );
  }

  batch.set(sessionFullRef(session.id), {
    contentTagId,
    fullMetadata: {
      companyTrainingManagerEmail: session.companyTrainingManagerEmail,
      companyTrainingManagerFirstName: session.companyTrainingManagerFirstName,
      companyTrainingManagerLastName: session.companyTrainingManagerLastName,
      endAt: session.endAt,
      shortCode: session.shortCode,
      startAt: session.startAt,
      timezone: session.timezone,
    },
    id: session.id,
    status: "active",
    title: session.title,
    updatedAt: serverTimestamp(),
  });

  batch.set(sessionRuntimeRef(session.id), {
    contentTagId,
    currentDeckId: firstDeckId,
    currentPage: 0,
    id: session.id,
    initializedAt: serverTimestamp(),
    initializedBy: actorUid,
    liveSummary: "Accueil",
    sessionId: session.id,
    status: "active",
    updatedAt: serverTimestamp(),
  });

  batch.update(sessionRef(session.id), {
    contentTagId,
    status: "active",
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après démarrage.");
  }

  await syncSessionIndexes(nextSession);
  await writeAuditEvent({
    actorUid,
    eventType: "session.started",
    metadata: {
      contentTagId,
      resourceCount,
      sessionId: session.id,
    },
  });

  return {
    contentTagId,
    decksCount: plan.decks.length,
    exercisesCount: plan.exercises.length,
    formationsCount: plan.formationProjections.length,
    questionnairesCount: plan.questionnaires.length,
    resourceCount,
    runtime,
    session: nextSession,
  };
}

export async function freezeSessionContent({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<SessionContentFreezeResult> {
  if (session.status !== "draft" && session.status !== "planned") {
    throw new Error("Seules les sessions brouillon ou planifiées peuvent être figées.");
  }

  return freezeAttachedFormationSnapshots({
    actorUid,
    onlyMissing: false,
    session,
  });
}

function sessionClosureSummary({
  actorUid,
  closedQuestionnaireCount,
  expectedParticipantCount,
  passedExerciseCount,
  participantIdsWithAttendanceReview,
  shortCodeDisabled,
  signedAttendanceCount,
  absentAttendanceCount,
}: {
  absentAttendanceCount: number;
  actorUid: string;
  closedQuestionnaireCount: number;
  expectedParticipantCount: number;
  participantIdsWithAttendanceReview: Set<string>;
  passedExerciseCount: number;
  shortCodeDisabled: boolean;
  signedAttendanceCount: number;
}): SessionClosureSummary {
  const participantWithoutAttendanceCount = Math.max(
    0,
    expectedParticipantCount - participantIdsWithAttendanceReview.size,
  );

  return {
    absentAttendanceCount,
    closedQuestionnaireCount,
    deliverablesStatus:
      participantWithoutAttendanceCount === 0
        ? "ready_for_generation"
        : "needs_attendance_review",
    endedAt: new Date(),
    endedBy: actorUid,
    expectedParticipantCount,
    passedExerciseCount,
    participantWithoutAttendanceCount,
    signedAttendanceCount,
    shortCodeDisabled,
  };
}

export async function closeTrainingSession({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<SessionClosureSnapshot> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  if (session.status !== "active") {
    throw new Error("Seules les sessions actives peuvent être clôturées.");
  }

  const [
    participantAccesses,
    participants,
    attendances,
    attendanceStatuses,
    questionnaires,
    exercises,
  ] = await Promise.all([
    listParticipantAccesses(session.id),
    listExpectedParticipants(session.id),
    listSessionAttendances(session.id),
    listSessionAttendanceStatuses(session.id),
    listSessionPublicQuestionnaires(session.id),
    listSessionPublicExercises(session.id),
  ]);
  const questionnairesToClose = questionnaires.filter(
    (questionnaire) =>
      questionnaire.runtimeStatus === "open" ||
      questionnaire.runtimeStatus === "closed_to_new_entries",
  );
  const exercisesToPass = exercises.filter(
    (exercise) => exercise.runtimeStatus === "active",
  );
  const participantIdsWithAttendanceReview = new Set([
    ...attendances.map((attendance) => attendance.participantSlotId),
    ...attendanceStatuses.map((status) => status.participantSlotId),
  ]);
  const shortCode = session.shortCode.trim().toUpperCase();
  const shortCodeDisabled = session.shortCodeStatus === "active";
  const summary = sessionClosureSummary({
    absentAttendanceCount: attendanceStatuses.length,
    actorUid,
    closedQuestionnaireCount: questionnairesToClose.length,
    expectedParticipantCount: participants.length,
    participantIdsWithAttendanceReview,
    passedExerciseCount: exercisesToPass.length,
    shortCodeDisabled,
    signedAttendanceCount: attendances.length,
  });
  const batch = writeBatch(firestore);

  batch.update(sessionRef(session.id), {
    endedAt: serverTimestamp(),
    endedBy: actorUid,
    shortCodeDisabledAt: serverTimestamp(),
    shortCodeStatus: "disabled",
    status: "ended",
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionPublicRef(session.id), {
    liveSummary: "Session clôturée",
    status: "ended",
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionFullRef(session.id), {
    "fullMetadata.closureSummary": {
      absentAttendanceCount: summary.absentAttendanceCount,
      closedQuestionnaireCount: summary.closedQuestionnaireCount,
      deliverablesStatus: summary.deliverablesStatus,
      endedAt: serverTimestamp(),
      endedBy: actorUid,
      expectedParticipantCount: summary.expectedParticipantCount,
      passedExerciseCount: summary.passedExerciseCount,
      participantWithoutAttendanceCount:
        summary.participantWithoutAttendanceCount,
      signedAttendanceCount: summary.signedAttendanceCount,
      shortCodeDisabled,
    },
    status: "ended",
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionRuntimeRef(session.id), {
    liveSummary: "Session clôturée",
    status: "ended",
    updatedAt: serverTimestamp(),
  });

  for (const questionnaire of questionnairesToClose) {
    const closureData = {
      closedAt: serverTimestamp(),
      runtimeStatus: "closed",
    };
    batch.update(
      sessionPublicSubDocRef(
        session.id,
        PUBLIC_QUESTIONNAIRES_COLLECTION,
        questionnaire.id,
      ),
      closureData,
    );
    batch.set(
      doc(
        firestore,
        SESSION_RUNTIME_COLLECTION,
        session.id,
        PUBLIC_QUESTIONNAIRES_COLLECTION,
        questionnaire.id,
      ),
      {
        closedAt: serverTimestamp(),
        correctionVisible: questionnaire.correctionVisible,
        id: questionnaire.id,
        sessionId: session.id,
        sourceQuestionnaireId: questionnaire.sourceQuestionnaireId,
        status: "closed",
        updatedAt: serverTimestamp(),
        versionId: questionnaire.versionId,
      },
      { merge: true },
    );
  }

  for (const exercise of exercisesToPass) {
    const closureData = {
      passedAt: serverTimestamp(),
      runtimeStatus: "passed",
    };
    batch.update(
      sessionPublicSubDocRef(session.id, PUBLIC_EXERCISES_COLLECTION, exercise.id),
      closureData,
    );
    batch.set(
      doc(
        firestore,
        SESSION_RUNTIME_COLLECTION,
        session.id,
        PUBLIC_EXERCISES_COLLECTION,
        exercise.id,
      ),
      {
        correctionVisible: exercise.correctionVisible,
        id: exercise.id,
        passedAt: serverTimestamp(),
        sessionId: session.id,
        sourceExerciseId: exercise.sourceExerciseId,
        status: "passed",
        updatedAt: serverTimestamp(),
        versionId: exercise.versionId,
      },
      { merge: true },
    );
  }

  if (shortCodeDisabled && shortCode) {
    batch.set(
      sessionShortCodeRef(shortCode),
      {
        disabledAt: serverTimestamp(),
        status: "disabled",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  await batch.commit();

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après clôture.");
  }

  await Promise.all([
    syncSessionIndexes(nextSession),
    ...participantAccesses
      .filter((access) => access.status === "active")
      .map((access) =>
        upsertSessionIndex({
          participantSlotId: access.participantSlotId,
          role: "participant",
          session: nextSession,
          uid: access.uid,
        }),
      ),
  ]);
  await writeAuditEvent({
    actorUid,
    eventType: "session.closed",
    metadata: {
      closedQuestionnaireCount: questionnairesToClose.length,
      participantWithoutAttendanceCount:
        summary.participantWithoutAttendanceCount,
      passedExerciseCount: exercisesToPass.length,
      sessionId: session.id,
      shortCodeDisabled,
    },
  });

  return {
    session: nextSession,
    summary,
  };
}

export async function archiveTrainingSession({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<TrainingSession> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  if (session.status !== "ended") {
    throw new Error("Seules les sessions clôturées peuvent être archivées.");
  }

  const participantAccesses = await listParticipantAccesses(session.id);
  const batch = writeBatch(firestore);

  batch.update(sessionRef(session.id), {
    archivedAt: serverTimestamp(),
    archivedBy: actorUid,
    status: "archived",
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionPublicRef(session.id), {
    liveSummary: "Session archivée",
    status: "archived",
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionFullRef(session.id), {
    status: "archived",
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après archivage.");
  }

  await Promise.all([
    syncSessionIndexes(nextSession),
    ...participantAccesses
      .filter((access) => access.status === "active")
      .map((access) =>
        upsertSessionIndex({
          participantSlotId: access.participantSlotId,
          role: "participant",
          session: nextSession,
          uid: access.uid,
        }),
      ),
  ]);
  await writeAuditEvent({
    actorUid,
    eventType: "session.archived",
    metadata: {
      participantAccessCount: participantAccesses.length,
      sessionId: session.id,
    },
  });

  return nextSession;
}

export async function readSessionRuntimeState(
  sessionId: string,
): Promise<SessionRuntimeState | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(
    doc(firestore, SESSION_RUNTIME_COLLECTION, sessionId).withConverter(
      sessionRuntimeConverter,
    ),
  );

  return snapshot.exists() ? snapshot.data() : null;
}

export function subscribeSessionRuntimeState({
  onError,
  onNext,
  sessionId,
}: {
  onError: (error: Error) => void;
  onNext: (runtime: SessionRuntimeState | null) => void;
  sessionId: string;
}): Unsubscribe {
  if (!firestore) {
    onNext(null);
    return () => undefined;
  }

  return onSnapshot(
    doc(firestore, SESSION_RUNTIME_COLLECTION, sessionId).withConverter(
      sessionRuntimeConverter,
    ),
    (snapshot) => {
      onNext(snapshot.exists() ? snapshot.data() : null);
    },
    onError,
  );
}

function clampSlidePage(page: number, pageCount: number): number {
  return Math.min(Math.max(0, Math.trunc(page)), Math.max(0, pageCount));
}

function publicDeckFromSnapshot(
  deckId: string,
  data: Record<string, unknown>,
): SessionPublicDeckProjection {
  const pageCount = typeof data.pageCount === "number" ? data.pageCount : 0;
  const visibleMaxPage =
    typeof data.visibleMaxPage === "number" ? data.visibleMaxPage : 0;

  return {
    id: typeof data.id === "string" ? data.id : deckId,
    order: typeof data.order === "number" ? data.order : 0,
    pageCount: Math.max(0, Math.trunc(pageCount)),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    sourceDeckId: typeof data.sourceDeckId === "string" ? data.sourceDeckId : "",
    title: typeof data.title === "string" ? data.title : "Support",
    versionId: typeof data.versionId === "string" ? data.versionId : "",
    visibleMaxPage: Math.max(0, Math.trunc(visibleMaxPage)),
  };
}

export async function updateSessionSlideNavigation({
  actorUid,
  deckId,
  manualVisibleMaxPage,
  page,
  sessionId,
}: {
  actorUid: string;
  deckId: string;
  manualVisibleMaxPage?: number;
  page: number;
  sessionId: string;
}): Promise<SessionRuntimeState> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const [runtimeSnapshot, deckSnapshot] = await Promise.all([
    getDoc(
      doc(firestore, SESSION_RUNTIME_COLLECTION, sessionId).withConverter(
        sessionRuntimeConverter,
      ),
    ),
    getDoc(sessionPublicSubDocRef(sessionId, PUBLIC_DECKS_COLLECTION, deckId)),
  ]);

  if (!runtimeSnapshot.exists()) {
    throw new Error("Runtime de session introuvable.");
  }

  if (!deckSnapshot.exists()) {
    throw new Error("Support public introuvable.");
  }

  const publicDeck = publicDeckFromSnapshot(
    deckSnapshot.id,
    asRecord(deckSnapshot.data()),
  );
  const nextPage = clampSlidePage(page, publicDeck.pageCount);
  const nextVisibleMaxPage =
    manualVisibleMaxPage === undefined
      ? Math.max(publicDeck.visibleMaxPage, nextPage)
      : clampSlidePage(manualVisibleMaxPage, publicDeck.pageCount);
  const liveSummary =
    nextPage === 0
      ? `${publicDeck.title} - accueil`
      : `${publicDeck.title} - page ${nextPage}/${publicDeck.pageCount}`;
  const batch = writeBatch(firestore);

  batch.update(sessionRuntimeRef(sessionId), {
    currentDeckId: publicDeck.id,
    currentPage: nextPage,
    liveSummary,
    updatedAt: serverTimestamp(),
  });
  batch.update(sessionPublicRef(sessionId), {
    liveSummary,
    updatedAt: serverTimestamp(),
  });
  batch.update(
    sessionPublicSubDocRef(sessionId, PUBLIC_DECKS_COLLECTION, publicDeck.id),
    {
      visibleMaxPage: nextVisibleMaxPage,
    },
  );

  await batch.commit();

  await writeAuditEvent({
    actorUid,
    eventType: "session.slideshow.navigation_updated",
    metadata: {
      deckId: publicDeck.id,
      manualVisibleMaxPage:
        manualVisibleMaxPage === undefined ? null : nextVisibleMaxPage,
      page: nextPage,
      sessionId,
      visibleMaxPage: nextVisibleMaxPage,
    },
  });

  const nextRuntime = await readSessionRuntimeState(sessionId);

  if (!nextRuntime) {
    throw new Error("Runtime de session introuvable après navigation.");
  }

  return nextRuntime;
}
